import { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Box, IconButton, Paper, Snackbar, Tab, Tabs } from '@mui/material';
import { ChatBubbleOutlined, Close, Folder, History, Mail, Speed } from '@mui/icons-material';
import { useQuery } from '@apollo/client/react';
import { GET_ACCESSES, GET_LICENSES_WHERE_IS_ADMIN, GET_USER } from '../user.graphql';
import { GET_ACCESSES_FOR_USER } from '../../pdf/pdf.graphql';
import { User } from '../../interfaces';
import { UserAccess } from '../interfaces';
import { Hero } from './Hero';
import { ProfilePanel } from './ProfilePanel';
import { AdminLicensesPanel } from './AdminLicensesPanel';
import { AccessesPanel } from './AccessesPanel';
import { ChangesPanel } from './ChangesPanel';
import { CommentsPanel } from './CommentsPanel';
import { NotificationsPanel } from './NotificationsPanel';
import { UsagePanel } from './UsagePanel';
import { CountChip } from './CountChip';

type TabId = 'access' | 'changes' | 'mail' | 'comments' | 'bruk';

export default function Details() {
  const params = useParams<{ id: string }>();
  const userId = Number(params.id);
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab: TabId = tabParam === 'changes' || tabParam === 'mail' || tabParam === 'comments' || tabParam === 'bruk' ? tabParam : 'access';
  const [emailErrorOpen, setEmailErrorOpen] = useState(false);

  const userQuery = useQuery<{ user: User }>(GET_USER, { variables: { id: userId } });
  const accessesQuery = useQuery<{ userAccess: UserAccess[] }>(GET_ACCESSES, { variables: { id: userId } });
  const singleDocAccessesQuery = useQuery<{ accesses: { id: number }[] }>(GET_ACCESSES_FOR_USER, { variables: { userId } });
  const adminQuery = useQuery<{ licenses: { id: number; companyName: string }[] }>(GET_LICENSES_WHERE_IS_ADMIN, {
    variables: { id: userId },
  });

  const activeAccesses = (accessesQuery.data?.userAccess ?? []).filter((x) => !x.isDeleted);
  const licenseGroupCount = new Set(activeAccesses.map((x) => x.license.id)).size;
  const totalAccessCount = activeAccesses.length;
  const singleDocAccessCount = singleDocAccessesQuery.data?.accesses.length ?? 0;
  const isActive = totalAccessCount + singleDocAccessCount > 0;
  const adminLicenseCount = adminQuery.data?.licenses.length ?? 0;

  const handleTabChange = (_: unknown, value: TabId) => {
    const next = new URLSearchParams(searchParams);
    if (value === 'access') next.delete('tab');
    else next.set('tab', value);
    setSearchParams(next, { replace: true });
  };

  return (
    <Box
      sx={{
        px: { xs: 1.5, sm: 2.5, md: 4 },
        pt: { xs: 1.5, sm: 2.5, md: 3 },
        pb: { xs: 6, sm: 8, md: 10 },
        maxWidth: 1280,
        mx: 'auto',
        boxSizing: 'border-box',
      }}
    >
      {userQuery.error && <span>Feil! {userQuery.error.message}</span>}
      <Hero
        user={userQuery.data?.user}
        isActive={isActive}
        totalAccesses={totalAccessCount + singleDocAccessCount}
        licenseGroupCount={licenseGroupCount}
        adminLicenseCount={adminLicenseCount}
      />

      <Box
        sx={{
          mt: { xs: 1.5, sm: 2.5 },
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(320px, 380px) 1fr' },
          gap: { xs: 1.5, sm: 2.5 },
          alignItems: 'start',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2.5 } }}>
          <ProfilePanel userId={userId} onLoginAlreadyUsed={() => setEmailErrorOpen(true)} />
          <AdminLicensesPanel userId={userId} />
        </Box>

        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{ borderBottom: 1, borderColor: 'divider', pl: { xs: 0, sm: 1.5 } }}
          >
            <Tab
              value="access"
              icon={<Folder fontSize="small" />}
              iconPosition="start"
              label={
                <TabLabel active={activeTab === 'access'} count={totalAccessCount}>
                  Tilganger
                </TabLabel>
              }
            />
            <Tab
              value="changes"
              icon={<History fontSize="small" />}
              iconPosition="start"
              label={<TabLabel active={activeTab === 'changes'}>Endringer</TabLabel>}
            />
            <Tab
              value="mail"
              icon={<Mail fontSize="small" />}
              iconPosition="start"
              label={<TabLabel active={activeTab === 'mail'}>E-poster</TabLabel>}
            />
            <Tab
              value="comments"
              icon={<ChatBubbleOutlined fontSize="small" />}
              iconPosition="start"
              label={<TabLabel active={activeTab === 'comments'}>Kommentarer</TabLabel>}
            />
            <Tab
              value="bruk"
              icon={<Speed fontSize="small" />}
              iconPosition="start"
              label={<TabLabel active={activeTab === 'bruk'}>Bruk</TabLabel>}
            />
          </Tabs>

          {activeTab === 'access' && <AccessesPanel userId={userId} />}
          {activeTab === 'changes' && <ChangesPanel userId={userId} />}
          {activeTab === 'mail' && <NotificationsPanel userId={userId} />}
          {activeTab === 'comments' && <CommentsPanel userId={userId} />}
          {activeTab === 'bruk' && <UsagePanel userId={userId} />}
        </Paper>
      </Box>

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        open={emailErrorOpen}
        autoHideDuration={3000}
        onClose={() => setEmailErrorOpen(false)}
        message="E-posten er allerede brukt."
        action={
          <IconButton color="inherit" size="small" onClick={() => setEmailErrorOpen(false)}>
            <Close fontSize="small" />
          </IconButton>
        }
      />
    </Box>
  );
}

function TabLabel({ active, count, children }: { active: boolean; count?: number; children: React.ReactNode }) {
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
      {children}
      {count !== undefined && (
        <CountChip count={count} active={active} />
      )}
    </Box>
  );
}
